import { useState } from "react";
import { Mail, Copy, Check, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const templates = {
  introduction: {
    subject: "Partner With Soar Your Visibility — Help Dress Colorado's Job Seekers for Success",
    body: (p) => `Dear ${p.partnerName || "[Partner Name]"},

My name is Christina Fraser, and I serve as Executive Director of Soar Your Visibility, a workforce-development program based in Colorado Springs, Colorado. Our mission is simple but urgent: ensure that no qualified candidate loses a job opportunity simply because they cannot afford appropriate interview attire.

Through a voucher-based model, we connect indigent and unemployed job seekers with local retail and nonprofit partners who provide professional clothing suitable for job interviews. Each beneficiary receives a digital voucher that can be redeemed at participating partners for interview-ready apparel — restoring dignity, confidence, and access to meaningful employment.

Why partner with us?

• Guaranteed reimbursement — vouchers are pre-funded and verified through our secure QR-based redemption system, so you're paid for every fulfilled voucher.
• Community impact & visibility — your business is featured as an official partner supporting Colorado's workforce, generating goodwill and new foot traffic.
• Effortless participation — our team handles beneficiary screening, voucher issuance, and tracking. You simply serve the customer and scan the voucher.
• Measurable outcomes — we provide reporting that documents the real economic impact your generosity helps create.

Becoming a partner takes just a short onboarding conversation. We'd love to schedule a brief 20-minute call to walk you through how the program works and answer any questions your team may have.

Would you be available for a call sometime next week? You can reply directly to this email or reach me at the contact information below.

Thank you for considering a partnership that changes lives, one interview at a time.

Warm regards,

Christina Fraser
Executive Director, Soar Your Visibility
Colorado Springs, CO
${p.phone || "[Phone]"} · ${p.email || "[Email]"} · ${p.website || "[Website]"}`,
  },
  followUp: {
    subject: "Following Up — A Quick Chat About Partnering With Soar Your Visibility",
    body: (p) => `Dear ${p.partnerName || "[Partner Name]"},

I wanted to follow up on my earlier note about partnering with Soar Your Visibility, our Colorado Springs–based workforce program that helps unemployed job seekers access professional interview attire through pre-funded vouchers.

I know your time is valuable, so I'll keep this brief: partnering with us requires no upfront cost, guarantees reimbursement for every voucher redeemed, and positions your business as a visible champion for Colorado's workforce.

Would a short 15–20 minute call work for you this week or next? I'm happy to work around your schedule.

Thank you again for your time and consideration.

Warm regards,

Christina Fraser
Executive Director, Soar Your Visibility
Colorado Springs, CO
${p.phone || "[Phone]"} · ${p.email || "[Email]"} · ${p.website || "[Website]"}`,
  },
  community: {
    subject: "Partnering to Serve Your Clients — Voucher Distribution With Soar Your Visibility",
    body: (p) => `Dear ${p.partnerName || "[Organization Name]"},

My name is Christina Fraser, and I serve as Executive Director of Soar Your Visibility, a workforce-development program based in Colorado Springs, Colorado. We provide vouchers that help indigent and unemployed job seekers obtain professional clothing suitable for job interviews — removing a barrier that too often stands between capable candidates and gainful employment.

Community organizations like yours are on the front lines, working directly with the individuals we aim to serve. That's why I'm reaching out: we'd like to partner with you to distribute vouchers to the clients you already support, ensuring they reach the people who need them most.

Here's how a partnership would work:

• We provide your organization with digital vouchers to issue to eligible clients as part of your existing services.
• Your clients redeem vouchers at our network of retail and nonprofit partners for interview-ready attire.
• We handle voucher tracking, redemption verification, and outcome reporting — no administrative burden on your team.
• Together, we expand access and measurably improve employment outcomes across our community.

I would love to schedule a brief meeting to introduce the program in more detail and discuss how voucher distribution could complement the work your organization is already doing. Would you have 30 minutes available in the coming weeks? I'm happy to meet in person here in Colorado Springs or connect virtually — whatever is most convenient for you and your team.

Please reply to this email or reach me at the contact information below, and I'll coordinate a time that works.

Thank you for the vital work you do for our community. I look forward to the opportunity to serve your clients together.

Warm regards,

Christina Fraser
Executive Director, Soar Your Visibility
Colorado Springs, CO
${p.phone || "[Phone]"} · ${p.email || "[Email]"} · ${p.website || "[Website]"}`,
  },
};

const Outreach = () => {
  const [tab, setTab] = useState("introduction");
  const [fields, setFields] = useState({ partnerName: "", phone: "", email: "", website: "" });
  const [copied, setCopied] = useState(false);

  const active = templates[tab];
  const bodyText = active.body(fields);

  const handleChange = (key) => (e) => setFields({ ...fields, [key]: e.target.value });

  const copyEmail = () => {
    const full = `Subject: ${active.subject}\n\n${bodyText}`;
    navigator.clipboard.writeText(full);
    setCopied(true);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const openMailClient = () => {
    const mailto = `mailto:?subject=${encodeURIComponent(active.subject)}&body=${encodeURIComponent(bodyText)}`;
    window.location.href = mailto;
  };

  return (
    <div className="space-y-6" data-testid="outreach-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Partner Outreach
        </h1>
        <p className="text-slate-500 mt-1">Ready-to-send email templates for introducing the program to potential partners</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personalization panel */}
        <Card className="dashboard-widget lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Personalize</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="partnerName">Partner Name</Label>
              <Input id="partnerName" data-testid="input-partner-name" className="mt-1"
                placeholder="e.g., JCPenney Colorado Springs" value={fields.partnerName} onChange={handleChange("partnerName")} />
            </div>
            <div>
              <Label htmlFor="phone">Your Phone</Label>
              <Input id="phone" data-testid="input-phone" className="mt-1"
                placeholder="(719) 555-0100" value={fields.phone} onChange={handleChange("phone")} />
            </div>
            <div>
              <Label htmlFor="email">Your Email</Label>
              <Input id="email" data-testid="input-email" className="mt-1"
                placeholder="christina@soaryourvisibility.org" value={fields.email} onChange={handleChange("email")} />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input id="website" data-testid="input-website" className="mt-1"
                placeholder="www.soaryourvisibility.org" value={fields.website} onChange={handleChange("website")} />
            </div>
          </CardContent>
        </Card>

        {/* Email preview */}
        <Card className="dashboard-widget lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-600" />
                Email Preview
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-2" onClick={copyEmail} data-testid="copy-email-btn">
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-2" onClick={openMailClient} data-testid="open-mail-btn">
                  <Send className="w-4 h-4" />
                  Open in Email
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} className="mb-4">
              <TabsList data-testid="template-tabs">
                <TabsTrigger value="introduction" data-testid="tab-introduction">Partner Intro</TabsTrigger>
                <TabsTrigger value="community" data-testid="tab-community">Community Org</TabsTrigger>
                <TabsTrigger value="followUp" data-testid="tab-followup">Follow-Up</TabsTrigger>
              </TabsList>
              <TabsContent value="introduction" />
              <TabsContent value="community" />
              <TabsContent value="followUp" />
            </Tabs>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Subject</div>
                <div className="font-semibold text-slate-900" data-testid="email-subject">{active.subject}</div>
              </div>
              <pre className="p-4 whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed" data-testid="email-body">
                {bodyText}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Outreach;
